import { Check } from "lucide-react";

function formatGBP(n) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
  }).format(n);
}

function computeDeposit(pkg, deposit) {
  if (!deposit) return null;
  return deposit.type === "percent"
    ? (pkg.price * deposit.value) / 100
    : Math.min(deposit.value, pkg.price);
}

function FeatureItem({ feature, isRec }) {
  const isViewingWebsite = /viewing website/i.test(feature);
  return (
    <li className="flex items-start gap-3 text-sm leading-relaxed">
      <Check
        className={`w-4 h-4 mt-0.5 shrink-0 ${isRec ? "text-[#CCBA56]" : "text-[#A9927D]"}`}
        strokeWidth={2}
      />
      <span className={isRec ? "text-white/90" : "text-[#5C5A56]"}>
        {isViewingWebsite ? (
          <button
            type="button"
            data-testid="viewing-website-link" data-track-click="viewing-website-anchor"
            onClick={() =>
              document
                .getElementById("viewing-website")
                ?.scrollIntoView({ behavior: "smooth" })
            }
            className={`text-left underline underline-offset-4 decoration-1 hover:opacity-70 transition-opacity cursor-pointer ${
              isRec ? "decoration-white/50" : "decoration-[#A9927D]"
            }`}
          >
            {feature}
          </button>
        ) : (
          feature
        )}
      </span>
    </li>
  );
}

function PriceBlock({ pkg, isRec, depositAmount }) {
  return (
    <>
      {pkg.original_price && pkg.original_price > pkg.price ? (
        <div className="flex items-center justify-center gap-2 mb-2">
          <span
            className={`text-base tabular-nums line-through ${
              isRec ? "text-white/50" : "text-[#8A8782]"
            }`}
          >
            {formatGBP(pkg.original_price)}
          </span>
          <span className="bg-[#CCBA56] text-[#1F1E1D] text-[10px] tracking-overline uppercase font-medium px-2 py-0.5 rounded-full">
            {formatGBP(pkg.original_price - pkg.price).replace(".00", "")} Off
          </span>
        </div>
      ) : null}
      <div
        className={`font-display text-4xl md:text-5xl tabular-nums tracking-tight leading-none mb-8 ${
          isRec ? "text-white" : "text-[#1F1E1D]"
        }`}
      >
        {formatGBP(pkg.price)}
      </div>
      {depositAmount ? (
        <div
          data-testid={`deposit-line-${pkg.id}`}
          className={`-mt-5 mb-8 text-sm ${isRec ? "text-white/75" : "text-[#5C5A56]"}`}
        >
          Secure your date with just{" "}
          <span className={`font-semibold ${isRec ? "text-[#CCBA56]" : "text-[#2F2E2E]"}`}>
            {formatGBP(depositAmount)}
          </span>
        </div>
      ) : null}
    </>
  );
}

function GuestbookFeature({ isRec }) {
  return (
    <li className="flex items-start gap-3 text-sm leading-relaxed">
      <Check
        className={`w-4 h-4 mt-0.5 shrink-0 ${isRec ? "text-[#CCBA56]" : "text-[#A9927D]"}`}
        strokeWidth={2}
      />
      <span className={isRec ? "text-white/90" : "text-[#5C5A56]"}>
        <button
          type="button"
          data-testid="guestbook-feature-link" data-track-click="guestbook-anchor"
          onClick={() =>
            document
              .getElementById("video-guestbook")
              ?.scrollIntoView({ behavior: "smooth" })
          }
          className={`text-left underline underline-offset-4 decoration-1 hover:opacity-70 transition-opacity cursor-pointer ${
            isRec ? "decoration-white/50" : "decoration-[#A9927D]"
          }`}
        >
          Video Messages Guestbook
        </button>{" "}
        <span
          className={`font-medium ${isRec ? "text-[#CCBA56]" : "text-[#B5A244]"}`}
        >
          Only included FREE for the next 7 days
        </span>
      </span>
    </li>
  );
}

function PreweddingFeature({ isRec, price }) {
  return (
    <li data-testid="prewedding-feature" className="flex items-start gap-3 text-sm leading-relaxed">
      <Check
        className={`w-4 h-4 mt-0.5 shrink-0 ${isRec ? "text-[#CCBA56]" : "text-[#A9927D]"}`}
        strokeWidth={2}
      />
      <span className={isRec ? "text-white/90" : "text-[#5C5A56]"}>
        Pre-Wedding Couple Photoshoot{" "}
        <span className={`font-medium ${isRec ? "text-[#CCBA56]" : "text-[#B5A244]"}`}>
          Included FREE (usually £{Number(price).toLocaleString("en-GB")})
        </span>
      </span>
    </li>
  );
}

export function PackageCard({ pkg, onSelect, deposit, showGuestbook, showPrewedding, preweddingPrice }) {
  const isUnavailable = !!pkg.unavailable;
  const isRec = pkg.recommended && !isUnavailable;
  const depositAmount = isUnavailable ? null : computeDeposit(pkg, deposit);
  return (
    <div
      data-testid={`package-card-${pkg.id}`}
      data-track-dwell={pkg.name}
      className={`relative flex flex-col transition-all duration-500 ${
        isUnavailable ? "" : "hover:-translate-y-1"
      } ${
        isRec
          ? "bg-[#21201F] text-white shadow-[0_20px_60px_rgba(0,0,0,0.18)]"
          : "bg-white border border-[#E5E3DB] text-[#1F1E1D]"
      } ${isUnavailable ? "saturate-[0.3] opacity-80" : ""}`}
    >
      {isUnavailable ? (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#8A3D3D] text-white px-4 py-1.5 text-[10px] tracking-overline uppercase font-medium rounded-full whitespace-nowrap z-10">
          Fully Booked
        </div>
      ) : isRec && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#CCBA56] text-[#1F1E1D] px-4 py-1.5 text-[10px] tracking-overline uppercase font-medium rounded-full whitespace-nowrap z-10">
          Recommended
        </div>
      )}
      <div className="p-8 md:p-10 pt-12 md:pt-14 text-center">
        <h3
          className={`font-display text-2xl md:text-3xl tracking-tight leading-tight mb-8 ${
            isRec ? "text-white" : "text-[#1F1E1D]"
          }`}
        >
          {pkg.name}
        </h3>
        <PriceBlock pkg={pkg} isRec={isRec} depositAmount={depositAmount} />
        {isUnavailable ? (
          <div
            data-testid={`unavailable-notice-${pkg.id}`}
            className="w-full border border-[#D8C2C2] bg-[#F7F1F1] text-[#8A3D3D] px-5 py-4 text-sm leading-relaxed text-left"
          >
            Sorry, we don't have availability for this package. Our team is
            already booked on your date.
          </div>
        ) : (
          <button
            onClick={() => onSelect(pkg)}
            data-testid={`select-package-${pkg.id}`} data-track-click={`select-package-${pkg.id}`}
            className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 text-xs tracking-overline uppercase font-medium transition-colors ${
              isRec
                ? "bg-[#CCBA56] hover:bg-[#B5A244] text-[#1F1E1D]"
                : "border border-[#2F2E2E] text-[#2F2E2E] hover:bg-[#2F2E2E] hover:text-white"
            }`}
          >
            <Check className="w-4 h-4" strokeWidth={2} />
            Select
          </button>
        )}
      </div>
      <ul
        className={`px-8 md:px-10 pb-10 pt-8 space-y-3 border-t ${
          isRec ? "border-white/15" : "border-[#E5E3DB]"
        }`}
      >
        {pkg.features.map((f, i) => (
          <FeatureItem key={`${pkg.id}-${f}-${i}`} feature={f} isRec={isRec} />
        ))}
        {showGuestbook && <GuestbookFeature isRec={isRec} />}
        {showPrewedding && <PreweddingFeature isRec={isRec} price={preweddingPrice} />}
      </ul>
    </div>
  );
}
